import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { IconAlertCircleFilled, IconInfoCircle, IconMail } from '@tabler/icons-react';

import appSettings from '../../app-settings';
import { templateTypes } from '../../core/constants';
import { countWarmUpsPendingReview, getWarmUpTimingLabels, selectEditableWarmUps } from '../../core/utils/warm-up';
import { useAccount } from '../../store/account/hooks';
import { useDomains } from '../../store/domains/hooks';
import Loader from '../loader/Loader';
import AlertMessage from '../alerts/AlertMessage';
import StepNavBottom from './StepNavBottom';
import WelcomeEmailAccordion from './WelcomeEmailAccordion';
import WarmUpEmailAccordion from './WarmUpEmailAccordion';
import { LaunchWelcomeSeriesButton } from '../welcome-series';

const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;
const CAMPAIGNS_URL = `${appSettings.baseUrl}/campaigns`;

const COUNT_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

// "Two one-time emails to your current supporters." -- sized to however many warm-ups were found.
const getWarmUpDescription = (count) => {
  if (!count) {
    return 'One-time emails to your current supporters. You\u2019ll launch them from the Campaigns page after onboarding.';
  }
  const countWord = COUNT_WORDS[count] ?? String(count);
  const emails = count === 1 ? 'email' : 'emails';
  const each = count === 1 ? 'it' : 'each one';
  const them = count === 1 ? 'it' : 'them';
  return `${countWord} one-time ${emails} to your current supporters. Review and save ${each} before continuing - you\u2019ll launch ${them} from the Campaigns page after onboarding.`;
};

const initialTemplates = [
  {
    title: 'Welcome Email #1',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 0,
  },
  {
    title: 'Welcome Email #2',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 1,
  },
  {
    title: 'Welcome Email #3',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 2,
  },
  {
    title: 'Welcome Email #4',
    image: 'letter.svg',
    source: 'welcome-4.json',
    type: templateTypes.recurring,
    order: 3,
  },
];

const WelcomeSeriesStep = ({ onPrev, onNext }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [templates, setTemplates] = useState(initialTemplates);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(null);
  const [warmUps, setWarmUps] = useState([]);
  const [activeWarmUpId, setActiveWarmUpId] = useState(null);
  const [warmUpsLoaded, setWarmUpsLoaded] = useState(false);

  const { account, getAccount } = useAccount();
  const { getDomains } = useDomains();

  const isLaunched = account?.welcomeSeriesLaunched;
  const hasTemplates = templates.some((t) => t.templateId);
  // Reviewing = saving once, which accepts the suggested campaign (suggested -> draft).
  const warmUpsPendingReview = countWarmUpsPendingReview(warmUps);
  const reviewedWarmUpCount = warmUps.length - warmUpsPendingReview;

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data: savedTemplates } = await axios.get(EMAIL_TEMPLATES_URL, {
        params: { type: templateTypes.recurring },
      });

      // Merge saved templates with initial templates
      const mergedTemplates = [...initialTemplates];
      savedTemplates.forEach(({ design, downloadUrl, order, templateId, templateVersion, emailConfig, type }) => {
        if (order >= 0 && order < 4) {
          mergedTemplates[order] = {
            ...mergedTemplates[order],
            design,
            downloadUrl,
            templateId,
            templateVersion,
            emailConfig,
            type,
          };
        }
      });

      setTemplates(mergedTemplates);
    } catch (err) {
      Sentry.captureException(err);
      setError(err?.message || 'Failed to load welcome series templates');
    }
    setLoading(false);
  }, []);

  // Warm-ups found here must be reviewed before Next. A load failure is treated as "none found"
  // (reported to Sentry) so a campaigns-api outage can't strand a customer in onboarding.
  const fetchWarmUps = useCallback(async () => {
    try {
      const { data: campaigns } = await axios.get(CAMPAIGNS_URL);
      setWarmUps(selectEditableWarmUps(campaigns));
    } catch (err) {
      Sentry.captureException(err);
    }
    setWarmUpsLoaded(true);
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchWarmUps();
    getDomains();
  }, [fetchTemplates, fetchWarmUps, getDomains]);

  const warmUpTimingLabels = useMemo(() => getWarmUpTimingLabels(warmUps), [warmUps]);

  const handleWarmUpSaveSuccess = () => {
    fetchWarmUps();
    setActiveWarmUpId(null);
  };

  const handleSaveSuccess = () => {
    fetchTemplates();
    setActiveAccordionIndex(null);
  };

  const handleLaunchSuccess = () => {
    getAccount();
  };

  if (loading) {
    return (
      <div className="relative flex h-96 max-w-4xl items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="m-auto max-w-4xl space-y-12 pb-[60px]">
      <div className="space-y-4">
        <h1 className="text-h1 font-semibold">Warm-up &amp; Welcome Series</h1>
        <p className="text-lg">
          The Warm-up emails are the first email campaigns you&apos;ll send to your current contacts. The Welcome Series
          introduces new potential donors to your organization every week.
        </p>
      </div>

      <section className="space-y-4" aria-labelledby="warm-up-emails-heading">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <h2 id="warm-up-emails-heading" className="text-h4 font-semibold">
              Warm-up emails
            </h2>
            <p className="text-base text-gray-600">{getWarmUpDescription(warmUps.length)}</p>
          </div>
          {warmUps.length > 0 && (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-10 px-2.5 py-0.5 text-sm text-gray-800">
              {`${reviewedWarmUpCount} of ${warmUps.length} reviewed`}
            </span>
          )}
        </div>
        {warmUps.length ? (
          <div className="bg-white divide-y divide-gray-50 rounded-lg border border-gray-100">
            {warmUps.map((campaign) => (
              <WarmUpEmailAccordion
                key={campaign.campaignId}
                campaign={campaign}
                timingLabel={warmUpTimingLabels[campaign.campaignId]}
                isOpen={activeWarmUpId === campaign.campaignId}
                onToggle={() => setActiveWarmUpId(activeWarmUpId === campaign.campaignId ? null : campaign.campaignId)}
                onSaveSuccess={handleWarmUpSaveSuccess}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white flex items-center gap-3 rounded-lg border border-dashed border-gray-200 p-6">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50">
              <IconMail size={20} className="text-primary-700" aria-hidden="true" />
            </div>
            <p className="text-base">
              The DonorSpring team is drafting your warm-up emails. We&apos;ll notify you when they&apos;re ready to
              review — you can keep going in the meantime.
            </p>
          </div>
        )}
      </section>

      <div className="space-y-6">
        <div className="space-y-1.5">
          <h2 className="text-h4 font-semibold">Welcome Series emails</h2>
          <p className="text-base text-gray-600">Sent automatically every week to new contacts.</p>
        </div>

        {hasTemplates ? (
          <div className="flex items-start space-x-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50">
              <IconInfoCircle size={20} className="text-primary-700" />
            </div>
            <p className="text-base">
              The DonorSpring team has created 4 draft Welcome Series emails below. Please review and customize the
              content of each email to fit your organization&apos;s voice and mission. Click &quot;Launch Welcome
              Series&quot; to indicate that they are ready to send.
            </p>
          </div>
        ) : (
          <div className="flex w-full justify-center">
            <div className="flex max-w-96 items-center gap-3 rounded-lg bg-primary-50 px-3 py-2">
              <IconAlertCircleFilled size={20} className="inline-block shrink-0 text-primary-700" />
              <p className="whitespace-pre-wrap text-base">
                The DonorSpring team is building out your Welcome Series and Content Calendar. We will notify you when
                they are ready for your review.
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-6">
        <div
          className={`bg-white divide-y divide-gray-50 rounded-lg border border-gray-100  ${!hasTemplates && 'pointer-events-none cursor-not-allowed bg-gray-10 text-gray-400'}`}
        >
          {templates.map((template, index) => (
            <WelcomeEmailAccordion
              key={`${template.title}-${index}`}
              template={template}
              isOpen={activeAccordionIndex === index}
              onToggle={() => {
                if (!hasTemplates) {
                  return;
                }
                setActiveAccordionIndex(activeAccordionIndex === index ? null : index);
              }}
              onSaveSuccess={handleSaveSuccess}
            />
          ))}
        </div>

        {hasTemplates && (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white-100 p-6">
            <div>
              <p className="font-semibold">Ready to launch?</p>
              <p className="text-sm text-gray-600">
                All 4 emails must be complete before you can launch your Welcome Series.
              </p>
            </div>
            <LaunchWelcomeSeriesButton onSuccess={handleLaunchSuccess} onError={setError} />
          </div>
        )}
      </div>

      {warmUpsPendingReview > 0 && (
        <p className="text-right text-sm text-gray-600">
          {`Review ${warmUpsPendingReview === 1 ? 'the remaining warm-up email' : `the ${warmUpsPendingReview} remaining warm-up emails`} to continue.`}
        </p>
      )}

      <StepNavBottom
        onPrev={onPrev}
        onNext={onNext}
        nextDisabled={!isLaunched || !warmUpsLoaded || warmUpsPendingReview > 0}
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

WelcomeSeriesStep.propTypes = {
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default WelcomeSeriesStep;
